import React, { useEffect, useState } from 'react';

import { hooks } from '../hooks';
import { svg } from '../assets/svg';
import { components } from '../components';
import apiService from '../service/ApiService';
import { Form, FormikProvider, useFormik } from 'formik';
import constants from '../helpers/constants';
import { Routes } from '../routes';
import * as yup from 'yup';
import utils from '../helpers/utils';
import { setLogin, setLogout } from '../store/slices/authSlice';
import dateHelper from '../helpers/dateHelper';
import { showSuccessToast } from '../components/Toast';

const validationSchema = yup.object({
  firstName: yup.string().required('First name is Required.'),
  surName: yup.string().required('Last name is Required.'),
  phone: yup
    .string()
    .required('Phone number is required.')
    .min(10, 'Phone number must be at least 10 digits.')
    .matches(/^[0-9]+$/, 'Phone number must only contain digits.'),
  email: yup
    .string()
    .email('Email is not in a valid format.')
    .required('Email is required.'),
  postCode: yup
    .string()
    .required('Postcode is Required.')
    .min(4, 'Postcode should have 4 digits.'),
  gender: yup.string().required('Choose your gender.'),
});

export const EditProfile: React.FC = () => {
  const navigate = hooks.useNavigate();
  const dispatch = hooks.useDispatch();

  const [opacity, setOpacity] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getUserProfile = async () => {
    setIsLoading(true);
    const { data } = await apiService.getUserProfileAsync();
    if (data) {
      formik.setValues({
        firstName: data.firstName,
        surName: data.surName,
        phone: data.phone,
        email: data.email,
        postCode: data.postCode,
        birthDate: dateHelper.displayDate(data.birthDate),
        gender: data.gender,
        showBirthDate: false,
        wantsUpdate: data.wantsEmailUpdates,
        wantsEmailUpdates: data.wantsEmailUpdates,
      });
    }
    setIsLoading(false);
  };
  useEffect(() => {
    getUserProfile();
  }, []);

  hooks.useScrollToTop();
  hooks.useOpacity(setOpacity);
  hooks.useThemeColor('#F6F9F9', '#F6F9F9', dispatch);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      surName: '',
      phone: '',
      email: '',
      postCode: '',
      birthDate: '',
      gender: '',
      showBirthDate: false,
      wantsUpdate: false,
      wantsEmailUpdates: false,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      setSubmitting(true);
      setIsLoading(true);
      const apiData = {
        firstName: values.firstName,
        surName: values.surName,
        phone: values.phone,
        email: values.email,
        gender: values.gender,
        birthDate: values.birthDate,
        postCode: values.postCode,
        wantsEmailUpdates: values.wantsUpdate,
      };
      // setIsSubmitting(false);
      const { data, error } = await apiService.updateUserProfileAsync(apiData);
      // setIsSubmitting(false);
      if (data) {
        const user = data.user;
        const token = data.session;
        const refreshToken = data.refresh;
        utils.setItemToStorage(constants.secureStore.loggedIn.userToken, token);
        utils.setItemToStorage(
          constants.secureStore.loggedIn.refreshToken,
          refreshToken
        );
        dispatch(
          setLogin({ user: user, token: token, refreshToken: refreshToken })
        );

        showSuccessToast('Profile updated successfully.');
        navigate(-1);
      } else if (error) {
        setErrors(error);
      }
      setSubmitting(false);
      setIsLoading(false);
    },
  });

  const handleOnDelete = async () => {
    setIsLoading(true);
    const { data, error } = await apiService.deleteUserProfileAsync();
    if (data) {
      showSuccessToast('Profile deleted successfully.');
      dispatch(setLogout());
      navigate(-1);
    } else if (error) {
      formik.setErrors(error);
    }
    setIsLoading(false);
  };

  const renderHeader = (): JSX.Element => {
    return <components.Header title='Edit Profile' showGoBack={true} />;
  };

  const renderContent = (): JSX.Element => {
    return (
      <main className='scrollable container'>
        <FormikProvider value={formik}>
          <Form autoComplete='off' noValidate onSubmit={formik.handleSubmit}>
            <section
              style={{
                backgroundColor: 'var(--white-color)',
                paddingLeft: 20,
                paddingRight: 20,
                borderRadius: 10,
                paddingTop: 50,
                paddingBottom: 30,
                marginTop: 20,
              }}
            >
              <components.Input
                placeholder='First Name'
                name='firstName'
                containerStyle={{ marginBottom: 25 }}
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.errors.firstName}
                touched={formik.touched.firstName}
                leftIcon={<svg.UserSvg />}
              />
              <components.Input
                placeholder='Last Name'
                name='surName'
                containerStyle={{ marginBottom: 25 }}
                value={formik.values.surName}
                onChange={formik.handleChange}
                error={formik.errors.surName}
                touched={formik.touched.surName}
                leftIcon={<svg.UserSvg />}
              />
              <components.Input
                placeholder='Email'
                name='email'
                leftIcon={<svg.MailSvg />}
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.errors.email}
                touched={formik.touched.email}
                // rightIcon={<svg.CheckSvg />}
                containerStyle={{ marginBottom: 25 }}
              />
              <components.Input
                placeholder='Phone Number'
                containerStyle={{ marginBottom: 25 }}
                name='phone'
                maxLength={10}
                inputMode={'numeric'}
                disabled={formik.values.phone}
                value={formik.values.phone}
                onKeyDown={utils.handleNumericKey}
                onChange={formik.handleChange}
                error={formik.errors.phone}
                touched={formik.touched.phone}
                leftIcon={<svg.PhoneSvg />}
              />
              <components.Input
                placeholder='Post Code'
                leftIcon={<svg.KeySvg />}
                name='postCode'
                maxLength={4}
                inputMode={'numeric'}
                onKeyDown={utils.handleNumericKey}
                value={formik.values.postCode}
                onChange={formik.handleChange}
                error={formik.errors.postCode}
                touched={formik.touched.postCode}
                containerStyle={{ marginBottom: 25 }}
              />
              <components.Input
                type='date'
                placeholder='BirthDate'
                name='birthDate'
                leftIcon={<svg.UserSvg />}
                value={formik.values.birthDate}
                onChange={formik.handleChange}
                error={formik.errors.birthDate}
                touched={formik.touched.birthDate}
                // rightIcon={<svg.EyeOffSvg />}
                containerStyle={{ marginBottom: 25 }}
              />
              <components.Input
                type='radio'
                name='gender'
                options={constants.refGender}
                leftIcon={<svg.Gender />}
                value={formik.values.gender}
                onChange={formik.handleChange}
                error={formik.errors.gender}
                onBlur={formik.handleBlur}
                touched={formik.touched.gender}
                // rightIcon={<svg.EyeOffSvg />}
                containerStyle={{ marginBottom: 25 }}
              />
              <div
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  display: 'flex',
                  margin: 5,
                }}
              >
                <input
                  type='checkbox'
                  id='wantsUpdate'
                  name='wantsUpdate'
                  className='input-field'
                  checked={formik.values.wantsUpdate}
                  style={{ marginRight: 5, marginLeft: 5 }}
                  onChange={formik.handleChange}
                />
                <label htmlFor='email-updates' className='t14'>
                  Yes, I would like to receive updates, offers and other
                  information by email.
                </label>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  padding: 5,
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <components.Button
                    text='Update'
                    containerStyle={{
                      paddingLeft: 10,
                      paddingRight: 10,
                      width: 100,
                      height: 40,
                      background: 'var(--text-button)',
                      border: 'none',
                    }}
                  />
                </div>
                <div>
                  <components.Button
                    text='delete'
                    colorScheme={'secondary'}
                    containerStyle={{
                      paddingLeft: 10,
                      width: 100,
                      paddingRight: 10,
                      height: 40,
                    }}
                    onClick={handleOnDelete}
                  />
                </div>
              </div>
              {/* <components.Button
                text="save changes"
                containerStyle={{ marginBottom: 20 }}
               
              /> */}
            </section>
          </Form>
        </FormikProvider>
      </main>
    );
  };

  return (
    <div id='screen' style={{ opacity }}>
      {renderHeader()}
      {isLoading ? <components.Loader /> : renderContent()}
    </div>
  );
};
